// Money and id text of the Payments UI. Money is an integer number of paise (`amountMinor`), INR. No floating point touches an amount: the
// whole rupees and the paise are cut with integer division, and an amount typed by a person is read from its TEXT, never through
// `parseFloat`.

const RUPEE_GROUPS = new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 });

/** "₹42,000.00" from 4_200_000. Another currency (an old quote) is shown as "USD 42,000.00". An unusable value is a dash. */
export function formatMinor(amountMinor: number | null | undefined, currency = "INR"): string {
  if (amountMinor === null || amountMinor === undefined || !Number.isSafeInteger(amountMinor)) {
    return "—";
  }
  const sign = amountMinor < 0 ? "-" : "";
  const absolute = Math.abs(amountMinor);
  const whole = Math.trunc(absolute / 100);
  const paise = String(absolute % 100).padStart(2, "0");
  const prefix = currency === "INR" ? "₹" : `${currency} `;
  return `${sign}${prefix}${RUPEE_GROUPS.format(whole)}.${paise}`;
}

/**
 * Reads the text of an amount a person typed ("1250", "1,250.5", "1250.50") into integer paise. Only digits, commas, spaces and at most one
 * point with one or two digits after it are accepted; anything else, zero, or more than 12 whole digits is `null`. It never uses a float.
 */
export function parseRupeesToMinor(text: string): number | null {
  const cleaned = text.replace(/[\s,]/g, "");
  const match = /^(\d{1,12})(?:\.(\d{1,2}))?$/.exec(cleaned);
  if (match === null) return null;
  const whole = Number(match[1]);
  const paise = Number((match[2] ?? "").padEnd(2, "0") || "0");
  const total = whole * 100 + paise;
  return Number.isSafeInteger(total) && total > 0 ? total : null;
}

/** "1250.50" from 125_050: the text a person can edit. Integer arithmetic only. */
export function minorToRupeeText(amountMinor: number): string {
  const whole = Math.trunc(amountMinor / 100);
  const paise = String(amountMinor % 100).padStart(2, "0");
  return `${whole}.${paise}`;
}

/** The first 8 characters of an id: enough to tell two rows apart on screen. The full id stays in the link and the title. */
export function shortId(id: string | null | undefined): string {
  return id === null || id === undefined || id === "" ? "—" : id.slice(0, 8);
}

/** The first 12 characters of a fingerprint (sha256 hex). */
export function shortFingerprint(fingerprint: string | null | undefined): string {
  return fingerprint === null || fingerprint === undefined || fingerprint === ""
    ? "—"
    : fingerprint.slice(0, 12);
}
